import { isAutoAssistantModel } from "@/utils/ai/modelCatalog";
import type { WingmanApiVersion } from "./client";
import { isWingmanV2OnlyRegion, trimTrailingSlash, wingmanLegacyBaseUrl, wingmanV2BaseUrl } from "./urls";

export const WINGMAN_V2_FLAG_KEY = "wingman-v2";

export type WingmanRouting = {
  serverURL: string;
  apiVersion: WingmanApiVersion;
};

export function resolveWingmanRouting(
  v2Enabled: boolean | null | undefined,
  model?: string | null
): WingmanRouting {
  const v2Base = wingmanV2BaseUrl();
  if (
    (isWingmanV2OnlyRegion() || v2Enabled === true || isAutoAssistantModel(model)) &&
    v2Base
  ) {
    return { serverURL: v2Base, apiVersion: "v2" };
  }
  return { serverURL: wingmanLegacyBaseUrl(), apiVersion: "legacy" };
}

// Conversation pins: a conversation stays on the deployment that created it.
// Routing later messages by the *current* flag can pair the wrong URL with the
// wrong contract. Pins are two-tier — in-memory (session), then localStorage
// (reload). Persistence is best-effort; storage failures degrade to session-only.
// Discovery rules (dual probe, rank, pin trust, Europe, outage fail-closed)
// live in docs/wingman-routing/README.md.
//
// Persisted pins are re-validated against the configured base URLs on read:
// pins are only ever written from env-derived URLs, so anything else in
// storage is corrupt or tampered and must not steer traffic (or auth tokens)
// to a foreign origin.

const PIN_STORAGE_PREFIX = "gail:wingman-routing:v1:";

type PersistedPin = WingmanRouting & { pinnedAt: number };

const pinnedRoutingByConversation = new Map<string, WingmanRouting>();

function pinStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? (window.localStorage ?? null) : null;
  } catch {
    return null;
  }
}

function isPersistedPin(value: unknown): value is PersistedPin {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.serverURL === "string" &&
    (candidate.apiVersion === "legacy" || candidate.apiVersion === "v2") &&
    typeof candidate.pinnedAt === "number"
  );
}

function isConfiguredRouting(pin: PersistedPin): boolean {
  if (isWingmanV2OnlyRegion()) {
    const expected = wingmanV2BaseUrl();
    return (
      pin.apiVersion === "v2" &&
      expected !== "" &&
      trimTrailingSlash(pin.serverURL) === expected
    );
  }
  const expected = pin.apiVersion === "v2" ? wingmanV2BaseUrl() : wingmanLegacyBaseUrl();
  return expected !== "" && trimTrailingSlash(pin.serverURL) === expected;
}

function readPersistedPin(conversationId: string): WingmanRouting | null {
  const storage = pinStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(PIN_STORAGE_PREFIX + conversationId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPersistedPin(parsed)) return null;
    if (!isConfiguredRouting(parsed)) return null;
    return { serverURL: parsed.serverURL, apiVersion: parsed.apiVersion };
  } catch {
    return null;
  }
}

function writePersistedPin(conversationId: string, routing: WingmanRouting): void {
  const storage = pinStorage();
  if (!storage) return;
  try {
    const pin: PersistedPin = { ...routing, pinnedAt: Date.now() };
    storage.setItem(PIN_STORAGE_PREFIX + conversationId, JSON.stringify(pin));
  } catch {
    // Session pin still applies if persistence fails.
  }
}

function deletePersistedPin(conversationId: string): void {
  const storage = pinStorage();
  if (!storage) return;
  try {
    storage.removeItem(PIN_STORAGE_PREFIX + conversationId);
  } catch {
    // ignore
  }
}

/** Return only an authoritative pin; unlike resolveConversationRouting, never guesses. */
export function getPinnedConversationRouting(
  conversationId: string
): WingmanRouting | null {
  const existing =
    pinnedRoutingByConversation.get(conversationId) ?? readPersistedPin(conversationId);
  if (existing) pinnedRoutingByConversation.set(conversationId, existing);
  return existing ?? null;
}

/** Existing conversations with no pin use the current flag/model guess; never persist that guess. */
export function resolveConversationRouting(
  conversationId: string,
  model?: string | null
): WingmanRouting {
  return getPinnedConversationRouting(conversationId) ?? resolveWingmanRouting(false, model);
}

/** Auto is v2-only. Hide it on conversations already pinned to legacy Wingman. */
export function isAutoSelectableForConversation(conversationId?: string | null): boolean {
  if (!conversationId) return true;
  return getPinnedConversationRouting(conversationId)?.apiVersion !== "legacy";
}

/** Pin on create/access; an existing pin always wins over a flag flip. */
export function pinConversationRouting(
  conversationId: string,
  routing: WingmanRouting
): WingmanRouting {
  const existing =
    pinnedRoutingByConversation.get(conversationId) ?? readPersistedPin(conversationId);
  if (existing) {
    pinnedRoutingByConversation.set(conversationId, existing);
    return existing;
  }
  pinnedRoutingByConversation.set(conversationId, routing);
  writePersistedPin(conversationId, routing);
  return routing;
}

/** Drop an expired conversation's pin so localStorage does not grow without bound. */
export function unpinConversationRouting(conversationId: string): void {
  pinnedRoutingByConversation.delete(conversationId);
  deletePersistedPin(conversationId);
}

export type ConversationRoutingProbeResult<T> = {
  routing: WingmanRouting;
  value: T;
};

export type ConversationHistoryRank = {
  messageCount: number;
  lastTimestampMs: number;
};

/**
 * Legacy `GetConversation` Galactus fallback cap. A live conversation whose
 * details key expired is re-hydrated at this limit; a write-once v2 shadow is
 * a short early snapshot, not a 250-message dump.
 */
export const LEGACY_GALACTUS_MESSAGE_LIMIT = 250;

/**
 * Dual-probe budget for the v2 leg. The chat client's 10-minute `timeoutMs` is
 * for streaming a completion. A hung v2 that accepted the connection must not
 * block a successful legacy read for that long.
 */
export const V2_DISCOVERY_PROBE_TIMEOUT_MS = 5_000;

export type ConversationRoutingProbeInit = {
  signal?: AbortSignal;
};

export type DiscoverConversationRoutingOptions<T> = {
  /**
   * Compared lexicographically (count, then last-message time). Higher count
   * wins so a full v2 Redis history beats a short Galactus/legacy shadow, but
   * only when the legacy count looks like a shadow (non-zero and below
   * `LEGACY_GALACTUS_MESSAGE_LIMIT`). A 0-length list is a cold stream; a
   * 250+ list is a truncated/live history. Treating either as "poorer" would
   * let v2 pin a live legacy conversation permanently. On a tie, legacy is
   * kept so a Galactus hydrate of the same projection does not steal.
   */
  rank?: (value: T) => ConversationHistoryRank;
  /**
   * Abort budget for the v2 dual-probe leg. Defaults to
   * `V2_DISCOVERY_PROBE_TIMEOUT_MS`. Ignored for Europe / v2-pin / legacy-only
   * paths, which have no fallback.
   */
  v2ProbeTimeoutMs?: number;
  /**
   * When Auto, prefer the v2 copy so a Galactus/legacy shadow cannot pin an
   * Auto conversation onto legacy Wingman. A legacy-only conversation still
   * loads if Auto is merely the currently selected assistant.
   */
  model?: string | null;
};

const toEpochMs = (value: Date | string | null | undefined): number => {
  if (value == null) {
    return 0;
  }
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * History rank for `discoverConversationRouting`: message count first, then
 * last-message time. Missing or invalid timestamps compare as epoch zero.
 */
export const rankConversationHistory = (params: {
  messageCount: number;
  lastTimestamp?: Date | string | null;
}): ConversationHistoryRank => ({
  messageCount: params.messageCount,
  lastTimestampMs: toEpochMs(params.lastTimestamp),
});

const isRicherHistory = (
  candidate: ConversationHistoryRank,
  incumbent: ConversationHistoryRank
): boolean =>
  candidate.messageCount !== incumbent.messageCount
    ? candidate.messageCount > incumbent.messageCount
    : candidate.lastTimestampMs > incumbent.lastTimestampMs;

/**
 * Legacy count that can be a write-once Galactus shadow of a v2 conversation.
 * 0 is a cold stream (append refreshes stream TTL, but a missed stream has no
 * fallback). >= 250 is the Galactus details fallback, not an early snapshot.
 */
const isPlausibleLegacyShadow = (incumbent: ConversationHistoryRank): boolean =>
  incumbent.messageCount > 0 &&
  incumbent.messageCount < LEGACY_GALACTUS_MESSAGE_LIMIT;

const abortReason = (signal: AbortSignal): Error => {
  if (signal.reason instanceof Error) return signal.reason;
  return Object.assign(new Error("v2 discovery probe timed out"), {
    name: "AbortError",
    cause: signal.reason,
  });
};

/** Rejects when `signal` aborts so a probe that ignores `init.signal` still unblocks. */
const rejectedWhenAborted = (signal: AbortSignal): Promise<never> =>
  new Promise((_, reject) => {
    const rejectWithReason = () => reject(abortReason(signal));
    if (signal.aborted) {
      rejectWithReason();
      return;
    }
    signal.addEventListener("abort", rejectWithReason, { once: true });
  });

function isNotFoundError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === 404
  );
}

const writeAuthoritativePin = (
  conversationId: string,
  routing: WingmanRouting
): WingmanRouting => {
  const existing = getPinnedConversationRouting(conversationId);
  // A details-only probe ranks neither side and would otherwise overwrite a
  // history load that already confirmed v2.
  if (existing?.apiVersion === "v2" && routing.apiVersion === "legacy") {
    return existing;
  }
  pinnedRoutingByConversation.set(conversationId, routing);
  writePersistedPin(conversationId, routing);
  return routing;
};

type ProbeHit<T> = {
  routing: WingmanRouting;
  value: T;
};

const toChosenResult = <T>(
  conversationId: string,
  chosen: ProbeHit<T>,
  alternate?: ProbeHit<T> | null
): ConversationRoutingProbeResult<T> => {
  const routing = writeAuthoritativePin(conversationId, chosen.routing);
  if (routing.apiVersion === chosen.routing.apiVersion) {
    return { routing, value: chosen.value };
  }
  if (alternate?.routing.apiVersion === routing.apiVersion) {
    return { routing, value: alternate.value };
  }
  // Pin declined to match chosen (typically no-downgrade to v2) but this probe
  // has no value for the pinned deployment. Returning the pin would pair v2
  // routing with legacy data. Keep this result internally consistent; the pin
  // in storage is unchanged.
  return { routing: chosen.routing, value: chosen.value };
};

/**
 * Resolve an existing conversation by probing its authoritative resource.
 *
 * A v2 pin is used directly. Europe probes v2 only. Elsewhere, when v2 is
 * configured, both deployments are probed so a Galactus/legacy shadow cannot
 * hide a longer v2 history; `rank` picks the richer copy and may upgrade a
 * stale legacy pin. The v2 leg is aborted after a short budget so a hang
 * cannot block a successful legacy read. Successful discovery is pinned for
 * subsequent reads and sends.
 */
export async function discoverConversationRouting<T>(
  conversationId: string,
  probe: (routing: WingmanRouting, init?: ConversationRoutingProbeInit) => Promise<T>,
  options?: DiscoverConversationRoutingOptions<T>
): Promise<ConversationRoutingProbeResult<T>> {
  const pinned = getPinnedConversationRouting(conversationId);

  if (isWingmanV2OnlyRegion()) {
    const v2 = resolveWingmanRouting(true);
    return toChosenResult(conversationId, { routing: v2, value: await probe(v2) });
  }

  if (pinned?.apiVersion === "v2") {
    return { routing: pinned, value: await probe(pinned) };
  }

  const legacy = resolveWingmanRouting(false);
  const v2 = resolveWingmanRouting(true);
  if (v2.apiVersion !== "v2") {
    return toChosenResult(conversationId, {
      routing: legacy,
      value: await probe(legacy),
    });
  }

  const v2ProbeTimeoutMs = options?.v2ProbeTimeoutMs ?? V2_DISCOVERY_PROBE_TIMEOUT_MS;
  const v2Controller = new AbortController();
  const v2TimeoutId = setTimeout(() => {
    v2Controller.abort(
      Object.assign(new Error("v2 discovery probe timed out"), { name: "AbortError" })
    );
  }, v2ProbeTimeoutMs);
  const v2Hang = rejectedWhenAborted(v2Controller.signal);
  // The hang promise outlives a fast v2 hit; swallow the later abort reject.
  void v2Hang.catch(() => undefined);

  let legacySettled: PromiseSettledResult<T>;
  let v2Settled: PromiseSettledResult<T>;
  try {
    [legacySettled, v2Settled] = await Promise.allSettled([
      probe(legacy),
      Promise.race([probe(v2, { signal: v2Controller.signal }), v2Hang]),
    ]);
  } finally {
    clearTimeout(v2TimeoutId);
    if (!v2Controller.signal.aborted) {
      v2Controller.abort();
    }
  }

  if (legacySettled.status === "rejected" && !isNotFoundError(legacySettled.reason)) {
    throw legacySettled.reason;
  }

  const legacyHit =
    legacySettled.status === "fulfilled"
      ? { routing: legacy, value: legacySettled.value }
      : null;
  const v2Hit =
    v2Settled.status === "fulfilled" ? { routing: v2, value: v2Settled.value } : null;

  if (legacyHit && v2Hit) {
    const rank = options?.rank;
    const chosen =
      isAutoAssistantModel(options?.model) ||
      (rank != null &&
        isPlausibleLegacyShadow(rank(legacyHit.value)) &&
        isRicherHistory(rank(v2Hit.value), rank(legacyHit.value)))
        ? v2Hit
        : legacyHit;
    return toChosenResult(conversationId, chosen, chosen === v2Hit ? legacyHit : v2Hit);
  }

  if (legacyHit) {
    return toChosenResult(conversationId, legacyHit);
  }

  if (v2Hit) {
    return toChosenResult(conversationId, v2Hit);
  }

  if (v2Settled.status === "rejected" && !isNotFoundError(v2Settled.reason)) {
    throw v2Settled.reason;
  }

  // Both probes rejected; legacy non-404 and v2 non-404 already thrown.
  if (legacySettled.status === "rejected") {
    throw legacySettled.reason;
  }
  throw new Error("Conversation not found");
}

/** @internal Test isolation only. */
export function resetConversationRoutingForTests(): void {
  pinnedRoutingByConversation.clear();
  const storage = pinStorage();
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(PIN_STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // ignore
  }
}
